import { WING_PATH } from '@/assets/icons/logo-geometry';

import {
  BIRD_MOTION,
  BIRD_SHAPES,
  type BirdShape,
  type BirdSpecies,
  type BirdStroke,
  WING_BEATS,
} from './agents-bird.species';

export type BirdFrame = { x: number; y: number; side: number };

type WingStroke = Extract<BirdStroke, { kind: 'wing' }>;

const WING_PIVOT = { x: 13, y: 7 };
const WING_GAP = 1;
const MIN_GAP_PX = 1.4;
const PROBE_SIZE = 280;
const PROBE_VIEW = { x: -3, y: -3, side: 31 };
const TARGET_COVERAGE = 0.3;
const FRAME_PADDING = 0.6;
const FALLBACK_FRAME: BirdFrame = { x: 0, y: 0, side: 26 };

const paths = new Map<string, Path2D>();
const frames = new Map<BirdSpecies, BirdFrame>();

const pathOf = (source: string) => {
  const existing = paths.get(source);
  if (existing) return existing;
  const path = new Path2D(source);
  paths.set(source, path);
  return path;
};

const placeWing = (
  context: CanvasRenderingContext2D,
  wing: WingStroke,
  wingBeat: number
) => {
  context.translate(wing.x, wing.y);
  context.rotate(((wing.rotate + wingBeat) * Math.PI) / 180);
  context.scale(wing.scale, wing.scale);
  context.translate(-WING_PIVOT.x, -WING_PIVOT.y);
};

const drawWing = (
  context: CanvasRenderingContext2D,
  wing: WingStroke,
  wingBeat: number,
  minGap: number
) => {
  const wingPath = pathOf(WING_PATH);
  context.save();
  placeWing(context, wing, wingBeat);
  context.globalCompositeOperation = 'destination-out';
  context.lineWidth = Math.max(WING_GAP, minGap) / wing.scale;
  context.lineJoin = 'round';
  context.stroke(wingPath);
  context.globalCompositeOperation = 'source-over';
  context.fill(wingPath);
  context.restore();
};

export const drawBirdShape = (
  context: CanvasRenderingContext2D,
  shape: BirdShape,
  scale: number,
  wingBeat = 0
) => {
  const minGap = MIN_GAP_PX / scale;
  for (const stroke of shape.strokes) {
    if (stroke.kind === 'wing') drawWing(context, stroke, wingBeat, minGap);
    else context.fill(pathOf(stroke.path));
  }
  context.globalCompositeOperation = 'destination-out';
  context.beginPath();
  for (const eye of shape.eyes) {
    context.moveTo(eye.x + eye.r, eye.y);
    context.arc(eye.x, eye.y, eye.r, 0, Math.PI * 2);
  }
  context.fill();
  context.globalCompositeOperation = 'source-over';
};

export const fillBirdWings = (
  context: CanvasRenderingContext2D,
  shape: BirdShape,
  wingBeat: number
) => {
  for (const stroke of shape.strokes) {
    if (stroke.kind !== 'wing') continue;
    context.save();
    placeWing(context, stroke, wingBeat);
    context.fill(pathOf(WING_PATH));
    context.restore();
  }
};

const probeInk = (shape: BirdShape, wingBeat: number) => {
  const canvas = document.createElement('canvas');
  canvas.width = PROBE_SIZE;
  canvas.height = PROBE_SIZE;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;
  const scale = PROBE_SIZE / PROBE_VIEW.side;
  context.setTransform(
    scale,
    0,
    0,
    scale,
    -PROBE_VIEW.x * scale,
    -PROBE_VIEW.y * scale
  );
  drawBirdShape(context, shape, scale, wingBeat);
  const pixels = context.getImageData(0, 0, PROBE_SIZE, PROBE_SIZE).data;
  let minX = PROBE_SIZE;
  let minY = PROBE_SIZE;
  let maxX = 0;
  let maxY = 0;
  let ink = 0;
  for (let y = 0; y < PROBE_SIZE; y++) {
    for (let x = 0; x < PROBE_SIZE; x++) {
      const alpha = pixels[(y * PROBE_SIZE + x) * 4 + 3];
      if (alpha <= 8) continue;
      ink += alpha / 255;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x + 1);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y + 1);
    }
  }
  return { minX, minY, maxX, maxY, ink, scale };
};

const buildFrame = (species: BirdSpecies): BirdFrame => {
  const shape = BIRD_SHAPES[species];
  const { swing } = BIRD_MOTION[species];
  const probes = [];
  for (const wingBeat of [
    0,
    Math.min(...WING_BEATS) * swing,
    Math.max(...WING_BEATS) * swing,
  ]) {
    const probe = probeInk(shape, wingBeat);
    if (!probe) return FALLBACK_FRAME;
    probes.push(probe);
  }
  const [rest] = probes;
  const minX = Math.min(...probes.map((probe) => probe.minX));
  const minY = Math.min(...probes.map((probe) => probe.minY));
  const maxX = Math.max(...probes.map((probe) => probe.maxX));
  const maxY = Math.max(...probes.map((probe) => probe.maxY));
  const restSide = Math.max(rest.maxX - rest.minX, rest.maxY - rest.minY);
  const coverage = rest.ink / (restSide * restSide);
  const weight = Math.max(1, Math.sqrt(coverage / TARGET_COVERAGE));
  const toUnits = (value: number) => value / rest.scale;
  const width = toUnits(maxX - minX);
  const height = toUnits(maxY - minY);
  const side = Math.max(width, height) * weight + FRAME_PADDING;
  return {
    x: PROBE_VIEW.x + toUnits(minX) + width / 2 - side / 2,
    y: PROBE_VIEW.y + toUnits(minY) + height / 2 - side / 2,
    side,
  };
};

export const birdFrame = (species: BirdSpecies) => {
  const existing = frames.get(species);
  if (existing) return existing;
  const frame = buildFrame(species);
  frames.set(species, frame);
  return frame;
};

export const renderBirdSilhouette = (
  species: BirdSpecies,
  pixels: number,
  wingBeat: number
) => {
  const canvas = document.createElement('canvas');
  canvas.width = pixels;
  canvas.height = pixels;
  const context = canvas.getContext('2d');
  if (!context) return canvas;
  const frame = birdFrame(species);
  const scale = pixels / frame.side;
  context.setTransform(scale, 0, 0, scale, -frame.x * scale, -frame.y * scale);
  drawBirdShape(context, BIRD_SHAPES[species], scale, wingBeat);
  return canvas;
};
